import { useEffect, useMemo } from 'react';
import { Navigate, useLocation, useParams } from 'react-router';
import z from 'zod';

import Layout from '@components/features/Layout';
import {
  subscribableLocalStorage,
  useLocalState,
  useLocalStateAction,
} from '@hooks/use-local-state';
import { useUserQuery } from '@queries/user';
import { monitoring } from '@services/monitoring';
import { useStrongParams } from '@utils/params';

const PREVIOUS_KEY = 'previous';
const NEW_TEAM_WINDOW_MS = 10 * 60 * 1000;

const storedParams = z.object({
  teamUuid: z.string(),
  appUuid: z.string().optional(),
});

type LocalTeamState = {
  lastAppUuid: string;
};

type LocalRootState = {
  teamUuid: string;
  appUuid?: string;
};

function getLocalTeamKey(teamUuid: string) {
  return `${teamUuid}-state`;
}

/**
 * Tracks the last visited team and app for the current user.
 */
export function TrackLocalTeamState() {
  const { teamUuid, appUuid } = useStrongParams(storedParams);
  const key = getLocalTeamKey(teamUuid);
  const setTeamState = useLocalStateAction<LocalTeamState>(key, undefined);
  const setRootState = useLocalStateAction<LocalRootState>(
    PREVIOUS_KEY,
    undefined
  );

  const { data } = useUserQuery();

  const teamMatch = useMemo(() => {
    return data?.teams?.find((team) => team.uuid === teamUuid);
  }, [data?.teams, teamUuid]);

  const appMatch = useMemo(() => {
    return teamMatch?.apps.find((app) => app.uuid === appUuid);
  }, [teamMatch, appUuid]);

  useEffect(() => {
    if (!teamMatch) return;
    const app = appMatch ?? teamMatch.apps.at(0);
    if (app) {
      setRootState({ teamUuid: teamMatch.uuid, appUuid: app.uuid });
      // Only set last app if we specifically navigated to an :appUuid query param
      if (appMatch) {
        setTeamState({ lastAppUuid: appMatch.uuid });
      }
    } else {
      setRootState({ teamUuid: teamMatch.uuid });
      setTeamState(undefined);
    }
  }, [teamMatch, appMatch, setTeamState, setRootState]);

  useEffect(() => {
    monitoring.setTeam(teamMatch);
    return () => monitoring.setTeam();
  }, [teamMatch]);

  useEffect(() => {
    if (teamMatch?.apps.length !== 1) return;
    const age = Date.now() - Date.parse(teamMatch.createdAt);
    if (!(age < NEW_TEAM_WINDOW_MS)) return;

    const [app] = teamMatch.apps;
    const key = `app-created-tracked-${app.uuid}`;
    try {
      if (subscribableLocalStorage.getItem(key)) return;
      subscribableLocalStorage.setItem(key, true);
    } catch {
      return;
    }
    monitoring.trackAction('app_created', {
      type: app.sandbox ? 'sandbox' : 'production',
    });
  }, [teamMatch]);

  return null;
}

export function useLocalTeamState(teamUuid: string) {
  const key = getLocalTeamKey(teamUuid);
  const [teamState] = useLocalState<LocalTeamState | undefined>(key, undefined);
  return teamState;
}

export function getLocalTeamState(teamUuid: string) {
  const key = getLocalTeamKey(teamUuid);
  return subscribableLocalStorage.getItem<LocalTeamState>(key) ?? undefined;
}

export function useFallbackTeamAppParams() {
  const { teamUuid, appUuid } = useParams();

  const { data } = useUserQuery();

  const [rootState] = useLocalState<LocalRootState | undefined>(
    PREVIOUS_KEY,
    undefined
  );
  const currentTeamUuid = teamUuid ?? rootState?.teamUuid;
  const team = useMemo(() => {
    const firstTeam = data?.teams?.at(0);
    if (!currentTeamUuid) return firstTeam;
    const match = data?.teams.find((team) => team.uuid === currentTeamUuid);
    return match ?? firstTeam;
  }, [currentTeamUuid, data?.teams]);

  const key = getLocalTeamKey(team?.uuid ?? '');
  const [teamState] = useLocalState<LocalTeamState | undefined>(key, undefined);
  const currentAppUuid = appUuid ?? teamState?.lastAppUuid;
  const app = useMemo(() => {
    if (!team) return undefined;
    const firstApp = team.apps.at(0);
    if (!currentAppUuid) return firstApp;
    const match = team.apps.find((app) => app.uuid === currentAppUuid);
    return match ?? firstApp;
  }, [currentAppUuid, team?.apps]);

  if (team && app) {
    return { teamUuid: team.uuid, appUuid: app.uuid };
  } else if (team) {
    return { teamUuid: team.uuid, appUuid: undefined };
  } else {
    return { teamUuid: undefined, appUuid: undefined };
  }
}

export function RedirectToFirstTeam() {
  const { teamUuid, appUuid } = useFallbackTeamAppParams();

  if (teamUuid && appUuid) {
    return <Navigate to={`/${teamUuid}/${appUuid}`} replace />;
  } else if (teamUuid) {
    return <Navigate to={`/${teamUuid}`} replace />;
  } else {
    return <Layout.Loader />;
  }
}

export function PrefixTeamPath() {
  const { teamUuid } = useFallbackTeamAppParams();
  const location = useLocation();

  if (teamUuid) {
    return <Navigate to={`/${teamUuid}${location.pathname}`} replace />;
  } else {
    return <Layout.Loader />;
  }
}

export function PrefixAppPath() {
  const { teamUuid, appUuid } = useFallbackTeamAppParams();
  const location = useLocation();

  if (teamUuid && appUuid) {
    // Remove the `/app` prefix from the path
    const path = location.pathname.replace(/^\/app/, '');
    return <Navigate to={`/${teamUuid}/${appUuid}${path}`} replace />;
  } else if (teamUuid) {
    // The team has no apps, so we redirect to the team root
    return <Navigate to={`/${teamUuid}`} replace />;
  } else {
    return <Layout.Loader />;
  }
}
